import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Group,
  Text,
  Alert,
  Stack,
  Stepper,
  Card,
  Select,
  LoadingOverlay,
  Title,
  Badge,
  Divider,
} from '@mantine/core';
import {
  IconUpload,
  IconSettings,
  IconCheck,
  IconAlertCircle,
  IconDeviceFloppy,
  IconSend,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { get } from 'idb-keyval';

import { MyUserDataContext } from '../../../contexts';
import { useGenevaSchemaService } from '../../settings/hooks/useGenevaSchemaService';
import { useGenevaRequestService } from '../../settings/hooks/useGenevaRequestService';
import { BaseSchema, TemplateDoc, RecordWithMetadata, BaseSchemaSchema } from '../../../types/geneva-schema';
import { ParsedFileData, FieldMapping, CreateBulkGenevaRequestData } from '../../../types/geneva-schema';
import { transformRowToFormData, FieldMetadataMap } from '../../../utils/file-parser';
import { getVisibleFieldsForRecord, convertToMappingFields, FieldWithMetadata, MappingField, findFieldOverride } from '../../../utils/geneva-field-helpers';
import { resolveMultiRecordFieldOverrides, getArrayFieldInfo } from '../../../utils/geneva-schema-helpers';
import { loadBaseSchema, applyDefaultHiddenOverrides } from '../../../utils/geneva-schema-loader';

import { BulkFileUpload } from './BulkFileUpload';
import { BulkFieldMapping } from './BulkFieldMapping';

interface BulkGenevaCreatorProps {
  orgId: string;
  templateId: string;
  onSave?: (requestId: string) => void | Promise<void>;
}

const CACHE_KEY = 'geneva_schema_cache2';

export const BulkGenevaCreator: React.FC<BulkGenevaCreatorProps> = ({
  orgId,
  templateId,
  onSave,
}) => {
  const userContext = useContext(MyUserDataContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Schema and template data
  const [schema, setSchema] = useState<BaseSchema | null>(null);
  const [template, setTemplate] = useState<TemplateDoc | null>(null);
  
  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<ParsedFileData | null>(null);
  const [fieldMappings, setFieldMappings] = useState<Record<string, FieldMapping>>({});
  const [bulkFormData, setBulkFormData] = useState<Record<string, any>[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const genevaService = useGenevaSchemaService({
    orgId,
    templateId,
    userId: userContext.user?.uid || 'bulk-user',
    userEmail: userContext.user?.email || 'bulk-user@example.com',
  });

  const requestService = useGenevaRequestService({
    orgId,
    userId: userContext.user?.uid || 'bulk-user',
    userEmail: userContext.user?.email || 'bulk-user@example.com',
    userDisplayName: userContext.user?.displayName || undefined,
  });

  // Load schema and template
  useEffect(() => {
    let mounted = true;
    
    const loadSchemaAndTemplate = async () => {
      try {
        if (!mounted) return;
        setLoading(true);
        setError(null);

        // Load schema (cache first, with network fallback)
        const schemaData = await loadBaseSchema();
        if (mounted) setSchema(schemaData);

        // Load template
        const templateData = await genevaService.loadTemplate();
        
        // Apply default hidden overrides centrally
        const modifiedTemplate = applyDefaultHiddenOverrides(templateData);
        if (mounted) setTemplate(modifiedTemplate);

      } catch (err) {
        console.error('Failed to load schema/template:', err);
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to load form configuration');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadSchemaAndTemplate();
    
    return () => {
      mounted = false;
    };
  }, [orgId, templateId, genevaService.loadTemplate]);

  // Get available records (same logic as regular form)
  const availableRecords = useMemo((): RecordWithMetadata[] => {
    if (!schema || !template) return [];

    const records: RecordWithMetadata[] = [];

    // Real records
    schema.records.forEach(record => {
      if (record && record.record && template.enabledRecords[record.record]) {
        const fieldOverrides = template.fieldOverrides[record.record] || {};
        records.push({
          ...record,
          enabled: true,
          fieldCount: record.fields?.length || 0,
          overriddenFieldCount: Object.keys(fieldOverrides).length,
          isVirtual: false,
        });
      }
    });

    // Virtual records (including multi-record)
    if (template.virtualRecords) {
      Object.entries(template.virtualRecords).forEach(([recordId, virtualRecord]) => {
        if (virtualRecord && virtualRecord.enabled && recordId) {
          // Handle multi-record virtual records
          if (virtualRecord.isMultiRecord && virtualRecord.baseRecords) {
            // For multi-record, we add it as a special entry that combines all records
            const allFields: any[] = [];
            let totalFieldCount = 0;
            
            // Validate that all base records exist
            const allBaseRecordsExist = virtualRecord.baseRecords.every(baseRecordName => 
              schema.records.find(r => r && r.record === baseRecordName)
            );
            
            if (allBaseRecordsExist) {
              virtualRecord.baseRecords.forEach(baseRecordName => {
                const baseRecord = schema.records.find(r => r && r.record === baseRecordName);
                if (baseRecord && baseRecord.fields) {
                  allFields.push(...baseRecord.fields);
                  totalFieldCount += baseRecord.fields.length;
                }
              });
              
              records.push({
                record: recordId,
                fields: allFields,
                enabled: true,
                fieldCount: totalFieldCount,
                overriddenFieldCount: virtualRecord.multiRecordConfig?.reduce((count, config) => 
                  count + Object.keys(config.fieldOverrides || {}).length, 0) || 0,
                isVirtual: true,
                isMultiRecord: true,
                baseRecords: virtualRecord.baseRecords,
                label: virtualRecord.label || recordId,
              });
            }
          } else if (virtualRecord.base) {
            // Handle single-record virtual records
            // For recursive inheritance, find the ultimate base record
            let ultimateBaseRecord = null;
            let currentRecordId = virtualRecord.base;
            
            // Walk the chain to find the ultimate base record
            while (currentRecordId) {
              const realRecord = schema.records.find(r => r && r.record === currentRecordId);
              if (realRecord) {
                ultimateBaseRecord = realRecord;
                break;
              }
              
              // Check if it's a virtual record
              const virtualBase = template.virtualRecords?.[currentRecordId];
              if (virtualBase?.base) {
                currentRecordId = virtualBase.base;
              } else {
                break;
              }
            }
            
            if (ultimateBaseRecord && ultimateBaseRecord.fields) {
              records.push({
                record: recordId,
                fields: ultimateBaseRecord.fields,
                enabled: true,
                fieldCount: ultimateBaseRecord.fields.length,
                overriddenFieldCount: Object.keys(virtualRecord.fieldOverrides || {}).length,
                isVirtual: true,
                baseRecord: virtualRecord.base, // Keep immediate parent for display
                label: virtualRecord.label || recordId,
              });
            }
          }
        }
      });
    }

    const filteredRecords = records.filter(record => record && record.record); // Final safety filter
    return filteredRecords;
  }, [schema, template]);

  // Auto-select first record if none selected
  useEffect(() => {
    if (availableRecords.length > 0 && !selectedRecord) {
      setSelectedRecord(availableRecords[0].record);
    }
  }, [availableRecords, selectedRecord]);

  // Get available fields for the selected record with template overrides applied
  const availableFieldMetadata = useMemo(() => {
    if (!selectedRecord || !schema || !template) return [];

    const selectedRecordData = availableRecords.find(r => r.record === selectedRecord);
    if (!selectedRecordData?.fields) return [];

    if (selectedRecordData.isMultiRecord && selectedRecordData.baseRecords && template.virtualRecords?.[selectedRecord]) {
      const virtualRecord = template.virtualRecords[selectedRecord];
      const metadata: FieldWithMetadata[] = [];

      virtualRecord.multiRecordConfig?.forEach((config) => {
        const baseRecord = schema.records.find(r => r.record === config.recordName);
        if (!baseRecord?.fields) {
          return;
        }

        const resolvedOverrides = resolveMultiRecordFieldOverrides(selectedRecord, config.recordName, template);

        baseRecord.fields.forEach((field) => {
          const arrayInfo = getArrayFieldInfo(field);
          const override = findFieldOverride(field, resolvedOverrides, arrayInfo);

          const fieldMetadata: FieldWithMetadata = {
            ...field,
            xmlTag: `${config.recordName}.${field.xmlTag}`,
            isRequired: override?.required ?? field.required,
            hasDefault: override?.default !== undefined,
            isReadOnly: override?.readOnly ?? false,
            isHidden: override?.hidden ?? false,
            defaultValue: override?.default,
            helpText: override?.help,
            options: override?.options,
            uniqueFieldId: arrayInfo
              ? `${config.recordName}.${arrayInfo.arrayName}.${field.xmlTag}`
              : `${config.recordName}.${field.xmlTag}`,
            arrayContext: arrayInfo ? `${config.recordName}.${arrayInfo.arrayName}` : undefined,
          };

          if (!fieldMetadata.isHidden) {
            metadata.push(fieldMetadata);
          }
        });
      });

      return metadata;
    }

    return getVisibleFieldsForRecord(selectedRecord, selectedRecordData.fields, template);
  }, [selectedRecord, schema, template, availableRecords]);

  const availableFields: MappingField[] = useMemo(
    () => convertToMappingFields(availableFieldMetadata),
    [availableFieldMetadata],
  );

  const fieldMetadataMap = useMemo<FieldMetadataMap>(() => {
    const map: FieldMetadataMap = new Map();

    availableFieldMetadata.forEach((field) => {
      const key = field.uniqueFieldId || field.xmlTag;
      map.set(key, {
        arrayContext: field.arrayContext,
      });

      if (!map.has(field.xmlTag)) {
        map.set(field.xmlTag, {
          arrayContext: field.arrayContext,
        });
      }
    });

    return map;
  }, [availableFieldMetadata]);

  const handleFileProcessed = useCallback((data: ParsedFileData) => {
    setParsedData(data);
    setActiveStep(1);
  }, []);

  const handleMappingComplete = useCallback((mappings: Record<string, FieldMapping>) => {
    setFieldMappings(mappings);
    
    if (!parsedData || !selectedRecord) return;

    // Check if this is a multi-record virtual record
    const selectedRecordData = availableRecords.find(r => r.record === selectedRecord);
    const isMultiRecord = selectedRecordData?.isMultiRecord || false;

    // Transform all rows to form data
    const transformedData: Record<string, any>[] = [];
    const errors: string[] = [];

    parsedData.rows.forEach((row, index) => {
      try {
        const formDataRow = transformRowToFormData(row, mappings, fieldMetadataMap);
        
        // Skip empty rows
        if (Object.keys(formDataRow).length === 0) return;
        
        if (isMultiRecord) {
          // Transform multi-record data: convert "RecordName.FieldName" back to nested structure
          const multiRecordData: Record<string, Record<string, any>> = {};
          
          Object.entries(formDataRow).forEach(([fieldKey, value]) => {
            if (fieldKey.includes('.')) {
              const [recordName, fieldName] = fieldKey.split('.', 2);
              if (!multiRecordData[recordName]) {
                multiRecordData[recordName] = {};
              }
              multiRecordData[recordName][fieldName] = value;
            } else {
              // Handle fields without record prefix (shouldn't happen in multi-record but just in case)
              if (!multiRecordData['_global']) {
                multiRecordData['_global'] = {};
              }
              multiRecordData['_global'][fieldKey] = value;
            }
          });
          
          // Convert to the format expected by multi-record processing
          const multiRecordFormData: Record<string, any> = {};
          Object.entries(multiRecordData).forEach(([recordName, recordData]) => {
            if (recordName !== '_global') {
              const recordKey = `${selectedRecord}__${recordName}`;
              multiRecordFormData[recordKey] = recordData;
            }
          });
          
          transformedData.push(multiRecordFormData);
        } else {
          // Regular single-record data
          transformedData.push(formDataRow);
        }
      } catch (error) {
        errors.push(`Error processing row ${index + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    });

    setBulkFormData(transformedData);
    setValidationErrors(errors);
    
    if (transformedData.length > 0) {
      setActiveStep(2);
    }
  }, [availableRecords, fieldMetadataMap, parsedData, selectedRecord]);

  const handleSaveBulkRequest = useCallback(async (submitForApproval: boolean = false) => {
    if (!selectedRecord || !parsedData || !fieldMappings || bulkFormData.length === 0) {
      notifications.show({
        title: 'Invalid Request',
        message: 'Please complete all steps before saving',
        color: 'red',
      });
      return;
    }

    try {
      setSaving(true);

      const validRecords = bulkFormData.length;
      const errorRecords = validationErrors.length;
      
      // Check if this is a multi-record virtual record
      const selectedRecordData = availableRecords.find(r => r.record === selectedRecord);
      const isMultiRecord = selectedRecordData?.isMultiRecord || false;

      const metadata = {
        recordType: selectedRecord,
        totalRecords: parsedData.rowCount,
        validRecords,
        errorRecords,
        isMultiRecord,
        ...(isMultiRecord ? { virtualRecordId: selectedRecord } : {}),
      };

      const bulkRequestData: CreateBulkGenevaRequestData = {
        orgId,
        templateId,
        recordType: selectedRecord,
        fieldMappings,
        parsedData,
        bulkFormData,
        metadata,
      };

      const requestId = await requestService.createBulkRequest(bulkRequestData);
      
      if (submitForApproval) {
        await requestService.submitBulkForApproval(requestId);
      }

      notifications.show({
        title: 'Success',
        message: submitForApproval 
          ? 'Bulk request submitted for approval successfully'
          : 'Bulk request saved successfully',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      if (onSave) {
        await onSave(requestId);
      }

    } catch (error) {
      console.error('Failed to save bulk request:', error);
      notifications.show({
        title: 'Save Failed',
        message: error instanceof Error ? error.message : 'Failed to save bulk request',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setSaving(false);
    }
  }, [selectedRecord, parsedData, fieldMappings, bulkFormData, validationErrors, orgId, templateId, requestService, onSave]);

  if (loading) {
    return (
      <Box pos="relative" h={400}>
        <LoadingOverlay visible />
        <Text ta="center" mt="xl">Loading Geneva form configuration...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Configuration Error"
        color="red"
      >
        {error}
        <Text size="sm" mt="xs">
          Please configure your Geneva schema in Settings → Geneva Schema first.
        </Text>
      </Alert>
    );
  }

  if (availableRecords.length === 0) {
    return (
      <Alert
        icon={<IconSettings size={16} />}
        title="No Records Enabled"
        color="blue"
      >
        No records are enabled in your template. Please enable some records in Settings → Geneva Schema first.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3}>Bulk Geneva Request Creator</Title>
          <Text size="sm" c="dimmed">
            Upload a file and create multiple Geneva requests at once
          </Text>
        </Box>

        {activeStep === 2 && bulkFormData.length > 0 && (
          <Group gap="sm">
            <Button
              onClick={() => handleSaveBulkRequest(false)}
              loading={saving}
              leftSection={<IconDeviceFloppy size={16} />}
              variant="outline"
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSaveBulkRequest(true)}
              loading={saving}
              leftSection={<IconSend size={16} />}
            >
              Submit for Approval
            </Button>
          </Group>
        )}
      </Group>

      {/* Stepper */}
      <Stepper active={activeStep} mb="xl">
        <Stepper.Step
          label="Upload File"
          description="Select and upload your data file"
          icon={<IconUpload size={18} />}
        />
        <Stepper.Step
          label="Map Fields"
          description="Map columns to Geneva fields"
          icon={<IconSettings size={18} />}
        />
        <Stepper.Step
          label="Review & Submit"
          description="Review data and submit"
          icon={<IconCheck size={18} />}
        />
      </Stepper>

      {/* Record Type Selection */}
      {availableRecords.length > 1 && (
        <Card withBorder mb="md">
          <Select
            label="Record Type"
            description="Select the type of Geneva record for bulk creation"
            value={selectedRecord}
            onChange={setSelectedRecord}
            data={availableRecords.map(record => ({
              value: record.record || '',
              label: record.isVirtual ? (record.label || record.record || '') : (record.record || ''),
            }))}
            disabled={activeStep > 0}
          />
        </Card>
      )}

      {/* Step Content */}
      {activeStep === 0 && (
        <BulkFileUpload
          onFileProcessed={handleFileProcessed}
          onError={setError}
          disabled={!selectedRecord}
          recordTypeName={selectedRecord || undefined}
          availableFields={availableFields}
          availableFieldMetadata={availableFieldMetadata}
        />
      )}

      {activeStep === 1 && parsedData && (
        <BulkFieldMapping
          parsedData={parsedData}
          availableFields={availableFields}
          onMappingComplete={handleMappingComplete}
        />
      )}

      {activeStep === 2 && bulkFormData.length > 0 && (
        <Stack gap="md">
          <Card withBorder>
            <Group justify="space-between" mb="sm">
              <Text fw={500}>Bulk Request Summary</Text>
              <Group gap="lg">
                <Badge color="blue" variant="light">
                  {bulkFormData.length} records to create
                </Badge>
                <Badge color={validationErrors.length > 0 ? 'yellow' : 'green'} variant="light">
                  {validationErrors.length} validation issues
                </Badge>
              </Group>
            </Group>

            <Stack gap="sm">
              <Group gap="lg">
                <Text size="sm">
                  <strong>Record Type:</strong> {selectedRecord}
                </Text>
                <Text size="sm">
                  <strong>Source File:</strong> {parsedData?.fileName}
                </Text>
                <Text size="sm">
                  <strong>Fields Mapped:</strong> {Object.keys(fieldMappings).length}
                </Text>
              </Group>

              {validationErrors.length > 0 && (
                <>
                  <Divider />
                  <Alert
                    icon={<IconAlertCircle size={16} />}
                    title="Validation Issues"
                    color="yellow"
                    variant="light"
                  >
                    <Stack gap="xs">
                      {validationErrors.slice(0, 5).map((error, index) => (
                        <Text key={index} size="sm">• {error}</Text>
                      ))}
                      {validationErrors.length > 5 && (
                        <Text size="sm" c="dimmed">
                          ... and {validationErrors.length - 5} more issues
                        </Text>
                      )}
                    </Stack>
                  </Alert>
                </>
              )}
            </Stack>
          </Card>

          <Alert
            color="blue"
            variant="light"
            title="Ready to Submit"
          >
            Your bulk request is ready. Once submitted, it will create {bulkFormData.length} Geneva records 
            after approval. You can save it as a draft first or submit directly for approval.
          </Alert>
        </Stack>
      )}

      {/* Navigation */}
      {activeStep > 0 && activeStep < 2 && (
        <Group justify="space-between" mt="xl">
          <Button
            variant="light"
            onClick={() => setActiveStep(prev => prev - 1)}
          >
            Back
          </Button>
          <Text size="sm" c="dimmed">
            Step {activeStep + 1} of 3
          </Text>
        </Group>
      )}
    </Box>
  );
};
